import { useState } from 'react';
import { Menu, MenuHandler, MenuItem, MenuList, IconButton } from '@material-tailwind/react';
import StatusChip from '@components/widgets/StatusChip';
import { ReactComponent as MoreVertIcon } from '@assets/icons/more-icon.svg';
import { format, isValid, parseISO } from 'date-fns';
import type { BrokerOrgInvite } from '@types';

interface Props {
  invites: BrokerOrgInvite[];
  isAdmin: boolean;
  onCancel: (inviteId: string) => void;
  onResend: (inviteId: string) => void;
}

const invitedOn = (value: string) => {
  if (!value) return '—';
  const date = parseISO(value);
  return isValid(date) ? format(date, 'dd/MM/yyyy') : '—';
};

const inviteTypeLabel = (type?: BrokerOrgInvite['type']) => {
  if (type === 'NEW_USER') return 'New user';
  if (type === 'EXISTING_USER') return 'Existing user';
  return '—';
};

const InvitesTab = ({ invites, isAdmin, onCancel, onResend }: Props) => {
  const [query, setQuery] = useState('');

  const filtered = invites.filter((inv) =>
    !query || inv.email.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      <div className="py-4">
        <input
          className="h-10 border border-grey-300 rounded-md px-3 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Search email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-grey-50 border-b border-grey-200">
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Email</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Invited</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Type</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-grey-500 uppercase tracking-wide">Status</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((inv) => (
              <tr key={inv.inviteId} className="border-b border-grey-100 hover:bg-grey-50">
                <td className="px-3 py-4 font-medium text-grey-900">{inv.email}</td>
                <td className="px-3 py-4 text-grey-500">{invitedOn(inv.invitedAt)}</td>
                <td className="px-3 py-4">
                  <span className="text-sm text-grey-700">{inviteTypeLabel(inv.type)}</span>
                </td>
                <td className="px-3 py-4">
                  <StatusChip variant="info3" label={inv.status === 'EXPIRED' ? 'Expired' : 'Pending'} />
                </td>
                <td className="px-3 py-4">
                  {isAdmin && (
                    <Menu>
                      <MenuHandler>
                        <IconButton variant="text" size="sm"><MoreVertIcon /></IconButton>
                      </MenuHandler>
                      <MenuList>
                        <MenuItem className="font-medium text-grey-700 text-sm" onClick={() => onResend(inv.inviteId)}>Resend</MenuItem>
                        <MenuItem className="font-medium text-rose-700 text-sm" onClick={() => onCancel(inv.inviteId)}>Cancel invitation</MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-grey-400 text-sm">No pending invites.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvitesTab;
